const FALLBACK_COUNTRY = 'DE';

// The country whose dial code a speaker of the language most likely has. Only used when the
// language tag names no region the phone input offers.
const LANGUAGE_TO_COUNTRY = {
  de: 'DE',
  en: 'GB',
  fr: 'FR',
  it: 'IT',
  es: 'ES',
  ca: 'ES',
  nl: 'NL',
  nb: 'NO',
  nn: 'NO',
  no: 'NO',
  sv: 'SE',
  da: 'DK',
  fi: 'FI',
  is: 'IS',
  pl: 'PL',
  pt: 'PT',
  cs: 'CZ',
  sk: 'SK',
  hu: 'HU',
  ro: 'RO',
  bg: 'BG',
  el: 'GR',
  hr: 'HR',
  sl: 'SI',
  et: 'EE',
  lv: 'LV',
  lt: 'LT',
  tr: 'TR',
  uk: 'UA',
  ga: 'IE',
  mt: 'MT',
  lb: 'LU',
  sq: 'AL',
  mk: 'MK',
};

const subtags = (tag) => (typeof tag === 'string' ? tag.split(/[-_]/) : []);

// The region is the last two-letter subtag after the language ('de-AT' → AT,
// 'zh-Hant-TW' → TW). The language subtag itself is skipped: 'be' is Belarusian, not Belgium.
const regionOf = (tag) =>
  subtags(tag)
    .slice(1)
    .filter((part) => /^[a-z]{2}$/i.test(part))
    .pop()
    ?.toUpperCase();

const countryOfLanguage = (tag) => LANGUAGE_TO_COUNTRY[subtags(tag)[0]?.toLowerCase()];

const firstSupported = (tags, toCountry, supported) =>
  tags.map(toCountry).find((country) => country && supported.includes(country));

/**
 * Picks the phone country for a visitor from the browser languages: first a supported region
 * named in any tag, then the country of the first mapped language, then the page locale.
 * Falls back to DE.
 *
 * A region in ANY tag wins over the language of the first tag: `['en', 'de-AT']` gives AT, not
 * GB. The region is the stronger signal for where the visitor's phone number is registered,
 * while a language (English above all) is often just a UI preference.
 * @param {object} params
 * @param {string[]} [params.languages] - navigator.languages, most preferred first
 * @param {string} [params.locale] - the page locale, e.g. 'de'
 * @param {string[]} params.supported - ISO codes the phone input offers
 * @returns {string} ISO 3166-1 alpha-2 code
 */
export const detectPhoneCountry = ({ languages = [], locale = '', supported }) => {
  const tags = Array.isArray(languages) ? languages : [];
  return (
    firstSupported(tags, regionOf, supported) ||
    firstSupported(tags, countryOfLanguage, supported) ||
    firstSupported([locale], countryOfLanguage, supported) ||
    FALLBACK_COUNTRY
  );
};
